import React, { useEffect, useMemo, useRef, useState } from "react";
import { SearchOutlined } from "@ant-design/icons";
import { Empty, Input, Popover, Spin, Tag, Typography } from "antd";
import enhancedApi, { masterDataService } from "../../api/enhancedApi";

const { Text } = Typography;

const POPOVER_WIDTH = 320;

const styles = {
    content: {
        width: POPOVER_WIDTH,
        padding: 10,
    },
    search: {
        marginBottom: 8,
    },
    list: {
        maxHeight: 280,
        overflowY: "auto",
    },
    row: {
        width: "100%",
        padding: "8px 10px",
        border: 0,
        borderRadius: 8,
        background: "transparent",
        cursor: "pointer",
        textAlign: "left",
    },
    rowHeader: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 8,
        marginBottom: 4,
    },
    routeName: {
        minWidth: 0,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    blockTime: {
        flexShrink: 0,
        color: "#1677ff",
        fontSize: 12,
        fontVariantNumeric: "tabular-nums",
    },
    tags: {
        display: "flex",
        flexWrap: "wrap",
        gap: 4,
        minHeight: 18,
    },
    empty: {
        padding: "18px 0 12px",
    },
};

function normalizeCode(value) {
    return String(value || "").trim().toUpperCase();
}

function normalizeSearchText(value) {
    return String(value || "")
        .toLowerCase()
        .replace(/[\s_\-–—>/\\]+/g, "");
}

function unwrapResponseData(response) {
    return response?.data?.data ?? response?.data ?? response;
}

function unwrapListResponse(response) {
    const data = unwrapResponseData(response);
    if (Array.isArray(data)) {
        return data;
    }
    if (Array.isArray(data?.items)) {
        return data.items;
    }
    return [];
}

function getAirportDisplayName(airport) {
    if (!airport) {
        return "";
    }

    return airport.airport_name || airport.name || airport.airport_name_en || airport.city || "";
}

function getAirportCode(airport) {
    return normalizeCode(airport?.iata_code || airport?.code || airport?.airport_code);
}

function formatBlockTime(minutes) {
    if (!minutes) {
        return "--";
    }
    return `${Math.floor(minutes / 60)}h${String(minutes % 60).padStart(2, "0")}`;
}

function routeMatchesAircraft(route, aircraftReg) {
    const normalizedReg = normalizeCode(aircraftReg);
    if (!normalizedReg || !Array.isArray(route?.compatible_aircraft) || route.compatible_aircraft.length === 0) {
        return false;
    }

    return route.compatible_aircraft.some((registration) => normalizeCode(registration) === normalizedReg);
}

function buildRouteLabels(route, aircraftReg) {
    const labels = [];
    const rawTags = Array.isArray(route?.tags) ? route.tags.filter(Boolean) : [];
    const hasPreferredTag = rawTags.some((tag) => ["preferred", "首选"].includes(String(tag).toLowerCase()));

    if (route?.is_default) {
        labels.push({ key: "default", text: "默认", color: "gold" });
    }
    if (routeMatchesAircraft(route, aircraftReg)) {
        labels.push({ key: "compatible", text: "适配", color: "cyan" });
    }
    if (route?.is_preferred || route?.preferred || hasPreferredTag) {
        labels.push({ key: "preferred", text: "首选", color: "blue" });
    }

    rawTags.forEach((tag) => {
        const text = String(tag);
        if (!labels.some((label) => label.text === text)) {
            labels.push({ key: `tag-${text}`, text, color: "purple" });
        }
    });

    return labels;
}

async function fetchAirportByCode(code) {
    const normalizedCode = normalizeCode(code);
    if (!normalizedCode) {
        return null;
    }

    try {
        const response = await masterDataService.airport.getAirportByCode(normalizedCode);
        return unwrapResponseData(response);
    } catch {
        return null;
    }
}

function buildSearchHaystack(route, airportsByCode) {
    const depCode = normalizeCode(route?.departure_airport);
    const arrCode = normalizeCode(route?.arrival_airport);
    const depAirport = airportsByCode[depCode];
    const arrAirport = airportsByCode[arrCode];

    return [
        route?.route_name,
        route?.departure_airport,
        route?.arrival_airport,
        route?.route_waypoints,
        getAirportDisplayName(depAirport),
        getAirportDisplayName(arrAirport),
        ...(Array.isArray(route?.alternate_airports) ? route.alternate_airports : []),
        ...(Array.isArray(route?.tags) ? route.tags : []),
    ].filter(Boolean).join(" ");
}

const RouteSearchPopover = ({
    dep,
    arr,
    aircraftReg,
    onSelect,
    onClose,
    children,
    open,
    defaultOpen = false,
    onOpenChange,
    placement = "rightTop",
}) => {
    const isControlled = open !== undefined;
    const [innerOpen, setInnerOpen] = useState(defaultOpen);
    const actualOpen = isControlled ? open : innerOpen;
    const [searchValue, setSearchValue] = useState("");
    const [routes, setRoutes] = useState([]);
    const [airportsByCode, setAirportsByCode] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const searchInputRef = useRef(null);

    const normalizedDep = normalizeCode(dep);
    const normalizedArr = normalizeCode(arr);
    const defaultSearchValue = normalizedDep && normalizedArr ? `${normalizedDep}-${normalizedArr}` : "";

    useEffect(() => {
        if (actualOpen) {
            setSearchValue(defaultSearchValue);
        }
    }, [actualOpen, defaultSearchValue]);

    useEffect(() => {
        if (!actualOpen) {
            return undefined;
        }

        const focusTimer = window.setTimeout(() => {
            searchInputRef.current?.focus?.({ cursor: "all" });
        }, 0);

        return () => window.clearTimeout(focusTimer);
    }, [actualOpen]);

    useEffect(() => {
        if (!actualOpen) {
            return undefined;
        }

        if (!normalizedDep || !normalizedArr) {
            setRoutes([]);
            setAirportsByCode({});
            setError(null);
            setLoading(false);
            return undefined;
        }

        let active = true;
        setLoading(true);
        setError(null);

        Promise.all([
            enhancedApi.flightRoute.getCompatibleRoutes(normalizedDep, normalizedArr, aircraftReg || null),
            fetchAirportByCode(normalizedDep),
            fetchAirportByCode(normalizedArr),
        ])
            .then(([routesResponse, depAirport, arrAirport]) => {
                if (!active) {
                    return;
                }

                const nextAirportsByCode = {};
                [depAirport, arrAirport].forEach((airport) => {
                    const code = getAirportCode(airport);
                    if (code) {
                        nextAirportsByCode[code] = airport;
                    }
                });

                setAirportsByCode(nextAirportsByCode);
                setRoutes(unwrapListResponse(routesResponse).filter((route) => route?.is_active !== false));
            })
            .catch((loadError) => {
                if (!active) {
                    return;
                }
                setRoutes([]);
                setAirportsByCode({});
                setError(loadError?.response?.data?.detail || loadError?.message || "航路加载失败");
            })
            .finally(() => {
                if (active) {
                    setLoading(false);
                }
            });

        return () => {
            active = false;
        };
    }, [actualOpen, aircraftReg, normalizedArr, normalizedDep]);

    const filteredRoutes = useMemo(() => {
        const needle = normalizeSearchText(searchValue);
        if (!needle) {
            return routes;
        }

        return routes.filter((route) => normalizeSearchText(buildSearchHaystack(route, airportsByCode)).includes(needle));
    }, [airportsByCode, routes, searchValue]);

    const setOpen = (nextOpen) => {
        if (!isControlled) {
            setInnerOpen(nextOpen);
        }
        onOpenChange?.(nextOpen);
        if (!nextOpen) {
            onClose?.();
        }
    };

    const handleSelect = (route) => {
        setOpen(false);
        onSelect?.(route);
    };

    const content = (
        <div
            style={styles.content}
            onMouseDown={(event) => event.stopPropagation()}
            onKeyDown={(event) => {
                if (event.key === "Escape") {
                    event.preventDefault();
                    event.stopPropagation();
                    setOpen(false);
                }
            }}
        >
            <Input
                ref={searchInputRef}
                allowClear
                prefix={<SearchOutlined />}
                placeholder={defaultSearchValue || "搜索航路"}
                value={searchValue}
                style={styles.search}
                onChange={(event) => setSearchValue(event.target.value)}
            />
            {loading ? (
                <div style={styles.empty}>
                    <Spin size="small" />
                </div>
            ) : error ? (
                <div style={styles.empty}>
                    <Text type="danger">{error}</Text>
                </div>
            ) : filteredRoutes.length ? (
                <div style={styles.list}>
                    {filteredRoutes.map((route) => {
                        const routeName = route.route_name || `${route.departure_airport || "--"}-${route.arrival_airport || "--"}`;
                        const labels = buildRouteLabels(route, aircraftReg);

                        return (
                            <button
                                key={route.id}
                                type="button"
                                style={styles.row}
                                onMouseEnter={(event) => {
                                    event.currentTarget.style.background = "#f5f8ff";
                                }}
                                onMouseLeave={(event) => {
                                    event.currentTarget.style.background = "transparent";
                                }}
                                onClick={() => handleSelect(route)}
                            >
                                <div style={styles.rowHeader}>
                                    <Text strong style={styles.routeName} title={routeName}>{routeName}</Text>
                                    <span style={styles.blockTime}>{formatBlockTime(route.block_time_minutes)}</span>
                                </div>
                                <div style={styles.tags}>
                                    {labels.map((label) => (
                                        <Tag key={label.key} color={label.color} style={{ margin: 0 }}>{label.text}</Tag>
                                    ))}
                                </div>
                            </button>
                        );
                    })}
                </div>
            ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={normalizedDep && normalizedArr ? "未找到匹配航路" : "请先提供 DEP/ARR"} style={styles.empty} />
            )}
        </div>
    );

    return (
        <Popover
            trigger="click"
            placement={placement}
            open={actualOpen}
            content={content}
            overlayInnerStyle={{ padding: 0 }}
            destroyTooltipOnHide
            onOpenChange={setOpen}
        >
            {children || <span />}
        </Popover>
    );
};

export default RouteSearchPopover;
